/* ==========================================================================
   RAM Bikes — proportional scaling engine
   ==========================================================================

   WHAT THIS DOES — AND WHAT IT DELIBERATELY NO LONGER DOES
   Every dimension in this project is expressed in rem against a 1440px
   Figma baseline where 1rem = 16px. At 1440 every token is the Figma value
   exactly.

   This module NO LONGER SCALES THE ROOT WITH THE VIEWPORT. The root is the
   reader's own default at every width. Letting it grow past 16 on a wide
   monitor would multiply every one of those exact values, making the largest
   rendering of the site the one furthest from the design — a 66px heading
   arriving at 80. Above 1440 the layout centres in its container instead.
   Below 1440 the fluid tokens in tokens.css do the adapting.

   An earlier version scaled DOWN below 1440 too, against a second mobile
   baseline of 390px, and the two ramps did not meet: crossing 768px changed
   every dimension by 28% in one pixel of resize, and the desktop ramp sat
   pinned at its 13px floor from 1170 all the way down to 768. Shrinking a
   composition is not responsive design.

   SO WHY IS THERE STILL A SCRIPT
   One reason, and it is the reason CSS cannot cover:

     ACCESSIBILITY. A hardcoded root font-size discards the reader's browser
     font-size preference, which fails WCAG 2.2 SC 1.4.4 (Resize Text). This
     module measures that preference — via the absolute `font-size: medium`
     keyword, which resolves to the browser default no matter what we have
     already set — and applies it. Someone who set their default to 24px gets
     a site at 24px; someone who left it alone gets exactly 16 and therefore
     exactly the design.

   It also reports `data-viewport` and `--scale` for anything that needs to
   branch on layout band, and reads `clientWidth` rather than `vw` so the
   scrollbar gutter is excluded from that measurement.

   OPT OUT
   Add `data-no-scale` to <html> to disable entirely (useful when debugging
   whether a bug is layout or scaling).

   LOAD ORDER
   Load this in <head> WITHOUT defer/async so the correct size is applied
   before first paint. It is deliberately dependency-free and tiny.
   ========================================================================== */

(function () {
  'use strict';

  var docEl = document.documentElement;

  if (docEl.hasAttribute('data-no-scale')) return;

  var CONFIG = {
    // Must match --design-width / --root-size-base in tokens.css.
    designWidth: 1440,
    baseSize: 16,

    // min === max === baseSize: the root NEVER scales, in either direction.
    // 1440 is the design width and at 1440 every token is the Figma value
    // exactly; letting the root grow past 16 would make the widest rendering
    // the one furthest from the design. Widening these re-introduces that.
    // See the "Root scaling" note in tokens.css.
    minRootPx: 16,
    maxRootPx: 16,

    // Reported on <html data-viewport> for CSS/JS that needs to branch on
    // layout class rather than raw width. Mirrors the four breakpoints
    // documented in tokens.css.
    breakpoints: { xs: 560, sm: 768, md: 992, lg: 1200 }
  };

  // The browser's default font size, in px. Starts at the spec default and is
  // corrected once the DOM can be measured.
  var userFontPx = 16;
  var measured = false;

  /* Measure the user's configured default font size.
     `font-size: medium` is an ABSOLUTE keyword: it resolves to the browser's
     default regardless of any font-size we have already set on <html>, which
     is exactly what makes it usable as a probe after we have taken over. */
  function measureUserFontPx() {
    var host = document.body || docEl;
    if (!host) return 16;
    var probe = document.createElement('div');
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText =
      'position:absolute!important;top:-9999px!important;left:-9999px!important;' +
      'visibility:hidden!important;pointer-events:none!important;' +
      'font-size:medium!important;width:1em!important;height:1em!important;' +
      'padding:0!important;border:0!important;margin:0!important;' +
      'max-width:none!important;min-width:0!important;';
    var px = 16;
    try {
      host.appendChild(probe);
      px = probe.getBoundingClientRect().width;
      host.removeChild(probe);
    } catch (e) {
      return 16;
    }
    // Guard against a hostile stylesheet or a zero-size measurement.
    return px > 4 && px < 80 ? px : 16;
  }

  function clamp(value, lo, hi) {
    return value < lo ? lo : value > hi ? hi : value;
  }

  function bandFor(vw) {
    var bp = CONFIG.breakpoints;
    if (vw < bp.xs) return 'xs';
    if (vw < bp.sm) return 'sm';
    if (vw < bp.md) return 'md';
    if (vw < bp.lg) return 'lg';
    return 'xl';
  }

  function compute() {
    // clientWidth excludes the scrollbar; innerWidth and CSS vw do not.
    var vw = docEl.clientWidth || window.innerWidth || CONFIG.designWidth;

    // Fold in the user's preference. The clamp bounds are scaled by the same
    // ratio, so a user who doubled their default font size gets a site that is
    // genuinely twice as large rather than one pinned to our ceiling.
    var userRatio = userFontPx / 16;
    var viewportScale = vw / CONFIG.designWidth;
    var ideal = CONFIG.baseSize * viewportScale * userRatio;
    var rootPx = clamp(ideal, CONFIG.minRootPx * userRatio, CONFIG.maxRootPx * userRatio);

    return {
      rootPx: rootPx,
      viewportScale: viewportScale,
      renderScale: rootPx / CONFIG.baseSize,
      band: bandFor(vw),
      viewportWidth: vw,
      userFontPx: userFontPx
    };
  }

  var last = null;

  function apply() {
    var s = compute();

    // Skip the write if nothing meaningfully changed — resize fires a lot.
    if (last && Math.abs(last.rootPx - s.rootPx) < 0.01 && last.band === s.band) {
      return s;
    }
    last = s;

    docEl.style.fontSize = s.rootPx + 'px';

    // Exposed for anything that needs the factor in JS-land — canvas, Mapbox
    // sizing, scroll animations — or in CSS via var(--scale).
    docEl.style.setProperty('--scale', String(Math.round(s.renderScale * 10000) / 10000));
    docEl.style.setProperty('--root-px', s.rootPx + 'px');
    docEl.style.setProperty('--viewport-scale', String(Math.round(s.viewportScale * 10000) / 10000));
    docEl.setAttribute('data-viewport', s.band);

    window.dispatchEvent(new CustomEvent('ram:scale', { detail: s }));
    return s;
  }

  // Apply immediately so first paint is already correct.
  apply();

  // Re-measure the user's font preference as soon as there is a body to
  // measure against, then re-apply. Before this point we assume 16.
  function calibrate() {
    if (measured) return;
    var px = measureUserFontPx();
    measured = true;
    if (px !== userFontPx) {
      userFontPx = px;
      last = null; // force a write
      apply();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', calibrate, { once: true });
  } else {
    calibrate();
  }

  // Coalesce resize storms into one write per frame.
  var frame = null;
  function onResize() {
    if (frame !== null) return;
    frame = window.requestAnimationFrame(function () {
      frame = null;
      apply();
    });
  }

  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('orientationchange', onResize, { passive: true });

  /* Belt and braces. Not every way the viewport can change dispatches a
     `resize` event — device emulation and devtools metric overrides are the
     common cases, and a browser chrome bar appearing or disappearing on
     mobile is another. Observing the root element catches all of them. */
  if (typeof ResizeObserver === 'function') {
    new ResizeObserver(onResize).observe(docEl);
  }

  // Browser zoom and OS font-size changes alter the `medium` keyword without
  // necessarily firing resize. Re-calibrate when the page is revisited.
  window.addEventListener('pageshow', function () {
    measured = false;
    calibrate();
  });

  window.RAMScale = {
    config: CONFIG,
    get: compute,
    refresh: function () {
      last = null;
      measured = false;
      calibrate();
      return apply();
    }
  };
})();
